// THE SHARED GRAMMAR OF THE TWO INSIGHTS DASHBOARDS — the arithmetic and the
// wording behind every number on Onboarding Insights and Selection Insights.
//
// PURE, STORE-FREE AND REACT-FREE, for the reason lib/window.ts gives one floor
// down: the SERVER computes these numbers out of sqlite and the CLIENT renders
// some of them, so the one definition of "what does this reading mean" has to be
// importable by both — and by a jest suite in a jsdom environment where
// better-sqlite3 will not load at all.
//
// ⚠️ IT SITS ON TOP OF lib/window.ts AND lib/shade-ramp.ts AND RESTATES
// NEITHER. The window arithmetic — which whole Sun-Sat weeks a reading covers,
// which week a date belongs to, what the preceding window is — is already
// written once and already read by /throughput; a second copy here would be two
// answers to "the last 4 weeks", free to disagree on a Sunday. The shading ramp
// is likewise one object: "darker means a higher number, and each row is shaded
// against its own weeks" is a claim a reader carries between tables, so this
// module only decides where a unit's ramp STARTS and hands the rest over.
//
// ⚠️ THE HONESTY RULES ARE THE PRODUCT, AND THEY LIVE HERE RATHER THAN IN THE
// MARKUP. Three of them, and each one is a place a dashboard usually lies:
//
//   A COUNTED ZERO IS `0`. AN ABSENT MEASUREMENT IS `—`. Never 0, never blank.
//   `Reading = number | null` is that distinction as a type, so a renderer
//   cannot flatten the two by accident and a `??  0` anywhere is visibly wrong.
//
//   A RATE OVER A ZERO DENOMINATOR IS `—`. Not 0%, which is a claim that
//   nothing succeeded, when in fact nothing was attempted.
//
//   WHAT IS EXCLUDED IS COUNTED, ONCE, IN THE FOOTER. A client with no video is
//   outside the median AND named below it — so `Measured` carries `excluded`
//   beside `value` and the two come out of ONE call. A footer computed
//   separately from the reading it annotates is a footer that goes stale the
//   first time the reading's rule changes.
//
// ⚠️ AND THE FOURTH, WHICH IS ABOUT TIME: A WEEK BEFORE A SERIES BEGAN IS `—`
// AND NOT `0`. Most of both pages is empty on the day it ships, and an axis of
// zeros stretching back before anything was ever recorded is the page asserting
// that nothing happened when the truth is that nobody was watching.
// `sinceSeries` is that rule, applied to a whole row at once.

import { shadeRamp } from "@/lib/shade-ramp";
import { currencySymbol } from "@/lib/weekly-report";
import { weekStartOf } from "@/lib/window";

/** The em dash every absent reading renders as. One string, one meaning. */
export const DASH = "—";

/**
 * A number, or `null` for "not measured".
 *
 * ⚠️ NULL IS NOT ZERO AND THE TYPE IS THE ENFORCEMENT. Every function here that
 * can fail to measure returns null, every renderer draws null as `DASH`, and
 * nothing in this module ever substitutes one for the other.
 */
export type Reading = number | null;

/**
 * WHAT KIND OF NUMBER A METRIC IS — a fact about the metric, not about how it
 * is drawn, which is why it sits beside the name in `MetricDef` and not in a
 * renderer's lookup table.
 *
 * It decides three things at once: the suffix on the value (`55%`, `2.7 d`),
 * the suffix on a DELTA of it (a rate rises in POINTS, never in per cent — the
 * single most common lie a dashboard tells), and where the shading ramp starts.
 *
 * ⚠️ THREE UNITS, WHERE /throughput HAS `count | percent | score`. These pages
 * have no score and they do have durations, which /throughput does not; the two
 * vocabularies overlap without being the same set, and `duration` is the one
 * carrying the decimal place. Renaming `percent` to `rate` follows how a rate's
 * delta is written (`▲ +9 pts`).
 *
 * ⚠️ AND THREE MORE, FOR AFFILIATE METRICS, whose numbers are not the insights
 * pages' shape. `fine-rate` is a rate measured in hundredths — a request rate is
 * 0.12%, and a whole-percent rounding would print every shop as `0%`. `usd` is whole dollars and `usd-cents` dollars and cents
 * (GMV per sample is $18.40, where $18 would hide the line at $22 being missed
 * by a dime). The dollar sign is the unit's PREFIX, carried in the digits, so a
 * tile, a cell and a delta (`▲ +$1,204`) spell it the same way.
 *
 * ⚠️ AND TWO FOR A SKILL PAGE'S CALLS TAB.
 * `decimal` is a plain ratio at one place (1.4 conversations a call), where a
 * count would round a batch of one and a half to 2. `usd-fine` is dollars to
 * the tenth of a cent: one DM Replies conversation costs about $0.011, and a new
 * skill version at $0.014 must not read as the same `$0.01` as the last one.
 */
export type DashUnit = "count" | "rate" | "fine-rate" | "duration" | "usd" | "usd-cents" | "decimal" | "usd-fine";

/**
 * WHICH DIRECTION IS GOOD — and the third answer, `neutral`, which is the one
 * most dashboards do not have.
 *
 * Demand metrics — clients started, briefs pasted, prospects saved — go up when
 * sales has a good month. A green arrow on one congratulates the agency for
 * something that is not its throughput, and a red one blames it for the
 * opposite; both are opinions the number does not support. They get the arrow,
 * which is a fact, and no colour.
 */
export type Polarity = "up-good" | "up-bad" | "neutral";

/**
 * ONE DESCRIPTION OF A METRIC, read by the tile, by its breakdown column and by
 * its over-time row: one renderer per number.
 *
 * The three renderings share this object so they cannot drift into formatting
 * the same fact differently — which is exactly what happened on /throughput
 * before METRICS was pulled out, and what `column` is deliberately the only
 * exception to.
 */
export interface MetricDef<K extends string = string> {
  key: K;
  /** The tile's label, and the over-time row's. */
  label: string;
  /**
   * The BREAKDOWN's column heading, when the label is too long to be one — and
   * the ONLY place a metric is allowed two names. It heads a column and never
   * travels with a number.
   */
  column?: string;
  /**
   * WHAT THE NUMBER MEANS, ON HOVER, never as permanent chrome. Seven
   * definitions printed under seven tiles is a wall of small type that nobody
   * reads and that pushes the numbers off the fold.
   */
  definition: string;
  unit: DashUnit;
  polarity: Polarity;
  /**
   * THE SIGN A MONEY UNIT PRINTS, where it isn't dollars (affiliate metrics: a
   * £ brand's own numbers stay in pounds). An ISO code; absent or USD is `$`. Only `usd`, `usd-cents` and `usd-fine` read it.
   */
  currency?: string | null;
}

// ─── Formatting one reading ──────────────────────────────────────────────────

/**
 * How many decimal places a unit carries.
 *
 * Durations get one and counts and rates get none, because that is the
 * precision each is actually measured to: "55%" of sixty-nine items is already
 * finer than the data, and "2.7 d" versus "3.1 d" is the whole signal on the
 * actionable-time tile.
 */
export const PLACES: Record<DashUnit, number> = { count: 0, rate: 0, "fine-rate": 2, duration: 1, usd: 0, "usd-cents": 2, decimal: 1, "usd-fine": 3 };

/**
 * The mark that PRECEDES a value, and a delta of it — `$1,204`, `▲ +$96`. Only
 * money has one. It travels inside the digits (`showReading`'s `text`), so no
 * renderer can drop it or put it on the wrong side of the sign.
 */
const VALUE_PREFIX: Record<DashUnit, string> = { count: "", rate: "", "fine-rate": "", duration: "", usd: "$", "usd-cents": "$", decimal: "", "usd-fine": "$" };

/** The mark that follows a value — `55%`, `2.7 d`, `5`. */
const VALUE_SUFFIX: Record<DashUnit, string> = { count: "", rate: "%", "fine-rate": "%", duration: "d", usd: "", "usd-cents": "", decimal: "", "usd-fine": "" };

/**
 * …and whether a space precedes it. `55%` is one token and `2.7 d` is a number
 * and its unit, which is how both are written everywhere else in the app.
 */
const SUFFIX_GAP: Record<DashUnit, string> = { count: "", rate: "", "fine-rate": "", duration: " ", usd: "", "usd-cents": "", decimal: "", "usd-fine": "" };

/**
 * The mark that follows a DELTA, which is not the same as the value's.
 *
 * ⚠️ A PERCENTAGE MOVES IN POINTS. "On-time went from 47% to 55%" is up eight
 * POINTS, not up eight per cent (which would be 50.8%), and printing `+8%` is
 * the most common lie in the genre. The unit table knows the difference, so no
 * renderer has to remember it.
 */
const DELTA_SUFFIX: Record<DashUnit, string> = { count: "", rate: " pts", "fine-rate": " pts", duration: " d", usd: "", "usd-cents": "", decimal: "", "usd-fine": "" };

/**
 * A number at its unit's precision, with `-0` normalised away.
 *
 * `-0` is what `Math.round` hands back for a small negative, it compares equal
 * to zero, and it prints as `-0` — a delta tag reading "▬ -0 d" beside a tile
 * that did not move.
 */
export function roundFor(unit: DashUnit, n: number): number {
  const f = 10 ** PLACES[unit];
  const r = Math.round(n * f) / f;
  return r === 0 ? 0 : r;
}

/**
 * The digits alone, with the unit's prefix, a trailing `.0` trimmed and the
 * thousands grouped: `20`, `2.7`, `55`, `9,937`, `$1,204`, `$18.40`.
 *
 * ⚠️ GROUPED SINCE AFFILIATE METRICS, where creators reached run to six figures:
 * `123456` beside `9,937` in the next cell is one number too many to count
 * digits in. Only a `.0` is trimmed, so a two-place unit keeps `0.10`.
 */
export function digitsFor(unit: DashUnit, n: number, currency?: string | null): string {
  const places = PLACES[unit];
  const text = formatterFor(places).format(roundFor(unit, n));
  return `${prefixFor(unit, currency)}${places === 1 ? text.replace(/\.0$/, "") : text}`;
}

/**
 * ONE `Intl.NumberFormat` PER PRECISION, made on first use and kept.
 * `toLocaleString` with options builds a new formatter on every call, and
 * Selection Insights' breakdown called it ~1,600 times a render (~34 ms on the
 * box). The locale is always `en-US` and the only option that varies is the
 * places, so four formatters cover every unit; `toLocaleString(locale, opts)` is
 * specified as exactly `new Intl.NumberFormat(locale, opts).format(n)`, and
 * dashboard-window.test.ts holds the two equal.
 */
const FORMATTERS = new Map<number, Intl.NumberFormat>();
function formatterFor(places: number): Intl.NumberFormat {
  let made = FORMATTERS.get(places);
  if (!made) {
    made = new Intl.NumberFormat("en-US", { minimumFractionDigits: places, maximumFractionDigits: places });
    FORMATTERS.set(places, made);
  }
  return made;
}

/** A money unit's sign in another currency (`currencySymbol`: £, €, "MXN "); every other unit's own prefix. */
function prefixFor(unit: DashUnit, currency?: string | null): string {
  return currency && currency !== "USD" && VALUE_PREFIX[unit] === "$" ? currencySymbol(currency) : VALUE_PREFIX[unit];
}

/**
 * A reading split into its digits, its unit mark and the space between them, so
 * a TILE can set the mark smaller than the number (the mockup's `20`+`d`) while
 * a table cell prints all three in a row.
 *
 * ONE FUNCTION FOR BOTH, because the alternative is a tile and a cell agreeing
 * about a number by coincidence. The SIZE of the mark is the tile's business;
 * everything else — the digits, the mark, the gap — is the unit's.
 */
export interface Shown {
  /** The digits, or `DASH` when there is nothing to show. */
  text: string;
  /** `%`, `d`, or empty — and always empty when `absent`. */
  suffix: string;
  /**
   * What separates the two — `""` before a `%`, a space before a `d`.
   *
   * ⚠️ IT TRAVELS WITH THE SUFFIX BECAUSE A TILE MUST NOT INVENT ITS OWN. The
   * strip drew the mark at a smaller size with a flat 2px margin, so the tiles
   * read `63 %` while every table cell beside them read `63%` — one fact,
   * spelled two ways, on one screen (found by eye against the fixtures). The
   * gap is a property of the unit, so it comes out of the same call the digits
   * and the mark do.
   */
  gap: string;
  /** True when this is a `—` rather than a number. */
  absent: boolean;
}

export function showReading(unit: DashUnit, value: Reading, currency?: string | null): Shown {
  if (value === null) return { text: DASH, suffix: "", gap: "", absent: true };
  return {
    text: digitsFor(unit, value, currency),
    suffix: VALUE_SUFFIX[unit],
    gap: SUFFIX_GAP[unit],
    absent: false,
  };
}

/** The same reading as one flat string, for a table cell: `55%`, `20 d`, `0`, `—`. */
export function cellText(unit: DashUnit, value: Reading, currency?: string | null): string {
  const shown = showReading(unit, value, currency);
  return `${shown.text}${shown.gap}${shown.suffix}`;
}

/**
 * "1 client", "2 clients" — every footer count is read as a SENTENCE, and
 * "1 clients with no video" reads as a bug in the page rather than as a count of
 * one, which is the wrong thing to say in the line whose whole job is to be
 * believed about the data.
 *
 * ⚠️ IT CAME OUT OF app/throughput/page.tsx, where it was private, for the
 * reason lib/shade-ramp.ts and lib/table-heading.ts each give in their own
 * headers: the second dashboard either imports it or copies it, and copying it
 * is how /throughput came to disagree with itself in the first place. Both read
 * this now.
 */
export function countNoun(n: number, singular: string, plural = `${singular}s`): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

// ─── Measuring, and what the measurement left out ────────────────────────────

/**
 * A READING AND ITS FOOTER, out of one call.
 *
 * ⚠️ `excluded` IS NOT AN ERROR COUNT. It is the number of members that had no
 * measurement to contribute — a client with no video, an item with no target
 * day — and the honesty rules require that they be outside the reading AND
 * named below it.
 * Computing the two separately is how a footer comes to describe a rule the
 * reading above it no longer uses.
 */
export interface Measured {
  /** The reading. Null when nothing could be measured — never 0. */
  value: Reading;
  /** How many members carried a measurement and are inside `value`. */
  counted: number;
  /** How many were set aside for want of one. The footer's number. */
  excluded: number;
}

/**
 * A plain count, which is the one reading that is honestly zero when nothing
 * happened: nought clients started really is nought, where a median over nought
 * clients is not a median of nought.
 *
 * `excluded` is the caller's, because what a count leaves out is a fact about
 * the query and not about the arithmetic — briefs with no brand mapping are
 * outside a per-brand count and nothing here could know it.
 */
export function countOf(n: number, excluded = 0): Measured {
  return { value: n, counted: n, excluded };
}

/**
 * The sum of a column of readings — the group row of a breakdown, and the
 * reading its rows must add up to.
 *
 * ⚠️ ALL-ABSENT SUMS TO `—`, NOT TO 0. A brand list where nobody has ever run a
 * brief has no total of zero briefs; it has no total. But a single counted zero
 * among the absences makes the sum a counted zero, because now something WAS
 * measured and it came to nothing.
 */
export function sumOf(values: Reading[]): Measured {
  const nums = values.filter((v): v is number => v !== null);
  return {
    value: nums.length ? nums.reduce((a, b) => a + b, 0) : null,
    counted: nums.length,
    excluded: values.length - nums.length,
  };
}

/**
 * The median of a column of readings, with the unmeasured ones set aside and
 * counted.
 *
 * ⚠️ THIS IS THE HONESTY RULES' NAMED CASE: a client with no video is excluded
 * from the median AND counted in the footer. Both halves, from one call, which
 * is what makes them impossible to disagree.
 *
 * An even count averages the two middles. Nothing is rounded here — the unit
 * decides that at format time, so a median and a delta of it round once, the
 * same way.
 */
export function medianOf(values: Reading[]): Measured {
  const nums = values.filter((v): v is number => v !== null).sort((a, b) => a - b);
  const mid = Math.floor(nums.length / 2);
  return {
    value: !nums.length ? null : nums.length % 2 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2,
    counted: nums.length,
    excluded: values.length - nums.length,
  };
}

/**
 * A rate, as a percentage — and `—` OVER A ZERO DENOMINATOR.
 *
 * Nought of nought is not 0%. "0% completed on time" is a claim that everything
 * measured missed its target; the truth is that nothing was measured, and the
 * two must not render alike.
 *
 * Unrounded on purpose: `showReading` rounds to the unit, and rounding twice is
 * how a rate and its own delta come to disagree by a point.
 */
export function rateOf(hits: number, of: number, excluded = 0): Measured {
  return { value: of === 0 ? null : (100 * hits) / of, counted: of, excluded };
}

/**
 * The same rate from a column of verdicts, where `null` is a member that could
 * not be judged — an item with no target day, a pitch with no recorded status.
 *
 * The unjudgeable are the denominator's exclusions, so the footer's count and
 * the rate's denominator come out of the same array.
 */
export function shareOf(verdicts: (boolean | null)[]): Measured {
  const judged = verdicts.filter((v): v is boolean => v !== null);
  return rateOf(judged.filter(Boolean).length, judged.length, verdicts.length - judged.length);
}

// ─── The delta ───────────────────────────────────────────────────────────────

/**
 * A tile's change against the immediately preceding window of equal length.
 *
 * ⚠️ AN ABSOLUTE CHANGE, NEVER A RATIO. Nought to four is not "+∞%" and it is
 * not "+400%"; there is no honest ratio against an empty window, so this never
 * computes one. Four more is what happened, and a rate moves in points.
 */
export interface Delta {
  /** The reading in the window being looked at, rounded to its unit. */
  now: Reading;
  /** …and in the window before it. */
  before: Reading;
  /**
   * `now − before`, or NULL when either side has no value at all.
   *
   * A median with nothing under it in the previous window is not a median of
   * zero, so "up 3 d" would be an invention. The page's two silences are the two
   * nulls here: a `Delta` of null (there is no preceding window — `All`) and a
   * `change` of null (there is one, and it was quiet).
   */
  change: number | null;
}

/**
 * ⚠️ THE SUBTRACTION IS OF THE ROUNDED VALUES, SO THE TILE IS CHECKABLE. A tile
 * showing 3.3 above a previous 2.7 must say +0.6; taking the difference of
 * 3.25 and 2.74 first would say +0.5 beside two numbers that visibly differ by
 * six tenths, and a reader who can do the sum in their head is the reader most
 * likely to stop trusting the page.
 */
export function deltaOf(unit: DashUnit, now: Reading, before: Reading): Delta {
  const a = now === null ? null : roundFor(unit, now);
  const b = before === null ? null : roundFor(unit, before);
  return { now: a, before: b, change: a === null || b === null ? null : roundFor(unit, a - b) };
}

/**
 * What colour a delta wears. `flat` and `none` are both grey and are NOT the
 * same sentence: one is "it did not move", the other is "moving is not good or
 * bad here".
 */
export type DeltaTone = "good" | "bad" | "flat" | "none";

/** The delta as the tile actually says it — glyph, text, colour and speech. */
export interface DeltaWords {
  /** ▲ ▼ ▬ — the ARITHMETIC direction. See the ⚠️ on `deltaWords`. */
  glyph: string;
  /** `+9 pts`, `−0.6 d`, `no change`. */
  text: string;
  /** The same for a screen reader, which cannot read a triangle. */
  spoken: string;
  tone: DeltaTone;
}

/**
 * ⚠️ THE GLYPH IS THE ARITHMETIC AND THE COLOUR IS THE JUDGEMENT: a favorable
 * move is green even when numerically negative. A duration falling
 * by six tenths of a day renders `▼ −0.6 d` IN GREEN: it went down, which is
 * what the arrow says, and that is good, which is what the colour says. Two
 * facts, two channels, neither one guessing at the other.
 *
 * ⚠️ AND THIS IS A DELIBERATE DEPARTURE FROM THE MOCKUP, WHICH WRITES THAT SAME
 * MOVE AS `▲ −0.6 d` — arrow and colour both meaning "better". The mockup is
 * self-consistent and it is not wrong on its own page; the trouble is that
 * /throughput already ships the other convention, and has since its redesign.
 * Two meanings for ▲ inside one app is a reader learning on one dashboard
 * something that is false on the next, which is worse than either convention
 * is good. So the app's existing one wins and the colour rule — the half that
 * is actually stated rather than shown — is honoured exactly. ▶ Reversible in
 * this one function if the mockup's convention is wanted after all.
 *
 * A REAL MINUS SIGN (U+2212), not a hyphen: it sits beside tabular numerals and
 * a hyphen reads a half-height too short next to them.
 */
export function deltaWords(unit: DashUnit, polarity: Polarity, change: number, currency?: string | null): DeltaWords {
  // Rounded ALREADY by `deltaOf`, and rounded again here so that a caller who
  // built a Delta by hand cannot make the words disagree with the tone: a
  // change of 0.04 on a duration prints as "no change" and must be toned flat,
  // not green. What is shown and what is claimed are the same number.
  const n = roundFor(unit, change);
  const flat = n === 0;
  const up = n > 0;

  const tone: DeltaTone = flat
    ? "flat"
    : polarity === "neutral"
      ? "none"
      : up === (polarity === "up-good")
        ? "good"
        : "bad";

  const size = `${digitsFor(unit, Math.abs(n), currency)}${DELTA_SUFFIX[unit]}`;

  return {
    glyph: flat ? "▬" : up ? "▲" : "▼",
    text: flat ? "no change" : `${up ? "+" : "−"}${size}`,
    spoken: flat ? "no change" : `${up ? "up" : "down"} ${size}`,
    tone,
  };
}

// ─── The weekly axis ─────────────────────────────────────────────────────────

/**
 * Drop dated items into the window's week columns.
 *
 * `weeks` is `weekStartsIn()` from lib/window.ts — this module never decides
 * which weeks exist, only which item belongs to which of them, so the axis a
 * table draws and the axis a tile is counted over cannot disagree.
 *
 * ⚠️ AN ITEM WITH NO DAY, OR ONE OUTSIDE THE AXIS, LANDS IN NO BUCKET AND IS
 * NOT AN ERROR. A post date set for next month is real and is simply in no
 * column yet; a week with no post date at all is a defect the FOOTER reports,
 * counted by the caller that knows what the missing thing was. Silently
 * dropping it into the first or last column would put a number in a week
 * nothing happened in.
 */
export function bucketByWeek<T>(
  weeks: string[],
  items: readonly T[],
  dayOf: (item: T) => string | null | undefined,
): T[][] {
  const at = new Map<string, number>();
  weeks.forEach((w, i) => at.set(w, i));

  const buckets: T[][] = weeks.map(() => []);
  for (const item of items) {
    const day = dayOf(item);
    if (!day) continue;
    const i = at.get(weekStartOf(day));
    if (i !== undefined) buckets[i].push(item);
  }
  return buckets;
}

/**
 * BLANK OUT THE WEEKS BEFORE THE SERIES BEGAN — the fourth honesty rule: a
 * week before a series began is `—` and not `0`.
 *
 * `since` is the day the underlying record STARTED EXISTING — the first
 * `sourcing.run` audit row, the day `onboarding.item_readiness` began being
 * written — not the first week that happens to hold data. The difference is the
 * whole point: a quiet week INSIDE the measured period is a counted zero,
 * because we were watching and nothing happened; a week before it is `—`,
 * because nobody was.
 *
 * ⚠️ `since` OF NULL BLANKS THE WHOLE ROW. A flow that has never recorded
 * anything has no zeros to draw, so the page names the act that writes its
 * first row instead of drawing an axis of them.
 *
 * The comparison is on the WEEK the series began, not the day: a series that
 * began on the Wednesday was being watched for that whole column, and half a
 * column is not something this axis can draw.
 */
export function sinceSeries(
  weeks: string[],
  values: Reading[],
  since: string | null | undefined,
): Reading[] {
  if (values.length !== weeks.length) {
    // A row that is not the axis's length is a bug in the caller, and the
    // failure it would otherwise produce — `undefined` cells rendering as
    // blanks — is exactly the ambiguous silence the honesty rules forbid.
    throw new Error(`sinceSeries: ${values.length} values for ${weeks.length} weeks`);
  }
  if (!since) return weeks.map(() => null);
  const first = weekStartOf(since);
  return weeks.map((w, i) => (w < first ? null : values[i]));
}

/**
 * TWELVE WEEKS TO A PAGE on the Over time tab.
 *
 * It is what stops the `All` window trying to draw two years of columns across
 * a laptop, and twelve rather than some other number because twelve is the
 * window control's widest real setting: a page of the table and a `12 weeks`
 * reading of the strip are the same span, so the outline lines up with the whole
 * table exactly once.
 */
export const WEEKS_PER_PAGE = 12;

/** How many pages of twelve a run of weeks makes — never fewer than one. */
export function pagesOf(weeks: number): number {
  return Math.max(1, Math.ceil(weeks / WEEKS_PER_PAGE));
}

/**
 * WHICH SLICE OF THE AXIS ONE PAGE SHOWS — `[start, end)` into the week list.
 *
 * ⚠️ NEWEST FIRST, AND PAGE 0 IS ALWAYS THE ONE ENDING THIS WEEK. `Older` walks
 * backwards, which is the direction a reader asks the question in ("and before
 * that?"), and it means the page a link lands on without a `?page=` is the one
 * the tiles above are counting.
 *
 * A page number past the end is CLAMPED rather than answered with an empty
 * slice: a truncated or stale link should land on the oldest page there is, not
 * on a table with no columns, which is a rendering bug looking for a place to
 * happen.
 */
export function weekPage(count: number, page: number): { start: number; end: number } {
  const safe = Math.min(Math.max(0, Math.trunc(page)), pagesOf(count) - 1);
  const end = Math.max(0, count - safe * WEEKS_PER_PAGE);
  return { start: Math.max(0, end - WEEKS_PER_PAGE), end };
}

/**
 * ONE ROW'S SHADES, from the app's one ramp (lib/shade-ramp.ts) — all this
 * decides is where the unit's scale starts.
 *
 * Counts run from ZERO, because a week with two really is a third of a week
 * with six. Rates and durations run from the row's own MINIMUM, because a band
 * of 68–90 laid on an absolute 0–100 scale renders as one flat wash and says
 * nothing at all.
 *
 * An absent reading gets no tint, which is what tells it apart from the
 * smallest real value in the row — the ramp's floor is deliberately not zero
 * for exactly that reason.
 */
export function shadesFor(unit: DashUnit, values: Reading[]): (string | undefined)[] {
  // Dollars are a flow like a count, and run from zero for the same reason.
  return shadeRamp(values, unit === "count" || unit === "usd" ? "zero" : "min");
}
